import { useRef, useCallback, useEffect, useState } from "react";
import { Logger } from "@cede/utils";
import { THROTTLE_DELAY, MAX_RECONNECT_ATTEMPTS, MAX_RECONNECT_DELAY } from "../constants";
import type { DirectOrderBookSnapshot, UseDirectOrderBookWSProps, UseDirectOrderBookWSResult } from "../types";

const EXCHANGE_ID = "okx";
const WS_URL = "wss://ws.okx.com:8443/ws/v5/public";

/**
 * Format pair symbol for OKX WebSocket
 * BTC/USDT → BTC-USDT
 */
function formatSymbol(pairSymbol: string): string {
  return pairSymbol.replace("/", "-");
}

// Orderbook state - maintained at module level to persist across re-renders
const orderbookState: {
  bids: Map<string, string>;
  asks: Map<string, string>;
  currentSymbol: string | null;
} = {
  bids: new Map(),
  asks: new Map(),
  currentSymbol: null,
};

/**
 * Hook for connecting to OKX WebSocket and subscribing to orderbook updates
 * OKX uses books channel (400 levels) with snapshot + delta pattern
 * First message is a snapshot, subsequent messages are deltas (size=0 means remove)
 */
export const useOKXOrderBook = ({
  pairSymbol,
  enabled = true,
}: UseDirectOrderBookWSProps): UseDirectOrderBookWSResult => {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const throttleTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const lastUpdateTimeRef = useRef<number>(0);
  const isConnecting = useRef(false);
  const reconnectAttemptsRef = useRef<number>(0);

  const [isConnected, setIsConnected] = useState(false);
  const [orderBookData, setOrderBookData] = useState<DirectOrderBookSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);

  const updateOrderBookData = useCallback((data: DirectOrderBookSnapshot) => {
    setOrderBookData(data);
    setError(null);
  }, []);

  const handleOrderBookUpdate = useCallback(
    (data: DirectOrderBookSnapshot) => {
      const now = Date.now();
      const timeSinceLastUpdate = now - lastUpdateTimeRef.current;

      if (timeSinceLastUpdate >= THROTTLE_DELAY) {
        lastUpdateTimeRef.current = now;
        updateOrderBookData(data);
      } else {
        if (throttleTimeoutRef.current) {
          clearTimeout(throttleTimeoutRef.current);
        }

        const remainingTime = THROTTLE_DELAY - timeSinceLastUpdate;
        throttleTimeoutRef.current = setTimeout(() => {
          lastUpdateTimeRef.current = Date.now();
          updateOrderBookData(data);
        }, remainingTime);
      }
    },
    [updateOrderBookData],
  );

  const disconnectWs = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }

    if (throttleTimeoutRef.current) {
      clearTimeout(throttleTimeoutRef.current);
      throttleTimeoutRef.current = undefined;
    }

    isConnecting.current = false;
    setIsConnected(false);
    lastUpdateTimeRef.current = 0;
  }, []);

  const subscribeToOrderBook = useCallback((ws: WebSocket, symbol: string) => {
    // books channel provides 400 levels with incremental updates (public, no auth required)
    // books5 provides top 5 levels only (public, no auth required)
    // books50-l2-tbt requires authentication - do not use
    const subscribeMessage = {
      op: "subscribe",
      args: [
        {
          channel: "books",
          instId: symbol,
        },
      ],
    };

    ws.send(JSON.stringify(subscribeMessage));
  }, []);

  /**
   * Convert module-level maps to sorted arrays and create snapshot
   */
  const createSnapshotFromState = useCallback((symbol: string): DirectOrderBookSnapshot => {
    // Convert bids map to sorted array (highest price first), limit to top 50
    const bids: [number, number][] = Array.from(orderbookState.bids.entries())
      .map(([price, size]) => [parseFloat(price), parseFloat(size)] as [number, number])
      .sort((a, b) => b[0] - a[0])
      .slice(0, 50);

    // Convert asks map to sorted array (lowest price first), limit to top 50
    const asks: [number, number][] = Array.from(orderbookState.asks.entries())
      .map(([price, size]) => [parseFloat(price), parseFloat(size)] as [number, number])
      .sort((a, b) => a[0] - b[0])
      .slice(0, 50);

    return {
      timestamp: Date.now(),
      exchangeId: EXCHANGE_ID,
      pairSymbol: symbol,
      bids,
      asks,
    };
  }, []);

  const connectWs = useCallback(() => {
    if (isConnecting.current || !enabled || !pairSymbol) return;

    disconnectWs();

    // Reset orderbook state only if symbol changed
    if (orderbookState.currentSymbol !== pairSymbol) {
      orderbookState.bids = new Map();
      orderbookState.asks = new Map();
      orderbookState.currentSymbol = pairSymbol;
    }

    isConnecting.current = true;
    setError(null);
    setOrderBookData(null);

    try {
      const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
        setIsConnected(true);
        isConnecting.current = false;
        reconnectAttemptsRef.current = 0;

        // Subscribe to orderbook
        const symbol = formatSymbol(pairSymbol);
        subscribeToOrderBook(ws, symbol);

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
      };

      ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle subscription response
          if (message.event === "subscribe") {
            return;
          }

          // Handle orderbook data
          // OKX format: { arg: { channel, instId }, action: "snapshot"|"update", data: [{ asks, bids, ts }] }
          if (message.data && message.data.length > 0) {
            const bookData = message.data[0];
            const isSnapshot = message.action === "snapshot";

            // On snapshot, reset state
            if (isSnapshot) {
              orderbookState.bids = new Map();
              orderbookState.asks = new Map();
            }

            // Apply bids: OKX format [[price, size, unknown, numOrders], ...]
            if (bookData.bids) {
              for (const level of bookData.bids as [string, string, string?, string?][]) {
                const price = level[0];
                const size = level[1];
                if (parseFloat(size) === 0) {
                  orderbookState.bids.delete(price);
                } else {
                  orderbookState.bids.set(price, size);
                }
              }
            }

            // Apply asks: OKX format [[price, size, unknown, numOrders], ...]
            if (bookData.asks) {
              for (const level of bookData.asks as [string, string, string?, string?][]) {
                const price = level[0];
                const size = level[1];
                if (parseFloat(size) === 0) {
                  orderbookState.asks.delete(price);
                } else {
                  orderbookState.asks.set(price, size);
                }
              }
            }

            // Create snapshot from updated state
            const snapshot = createSnapshotFromState(pairSymbol);
            handleOrderBookUpdate(snapshot);
          }
        } catch (err) {
          const errorMessage = err instanceof Error ? err.message : "Unknown error";
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to parse orderbook update:`, errorMessage, err);
          setError("Failed to parse orderbook update");
        }
      };

      ws.onerror = () => {
        isConnecting.current = false;
        setIsConnected(false);
        setError("WebSocket connection error");
      };

      ws.onclose = () => {
        isConnecting.current = false;
        setIsConnected(false);

        if (enabled && pairSymbol && reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), MAX_RECONNECT_DELAY);
          reconnectAttemptsRef.current++;

          Logger.log(
            `[${EXCHANGE_ID}OrderBookWS] Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})`,
            Logger.types.INFO,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            connectWs();
          }, delay);
        } else if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
          Logger.error(`[${EXCHANGE_ID}OrderBookWS] Max reconnection attempts reached, giving up`);
          setError("Failed to reconnect after multiple attempts");
        }
      };

      wsRef.current = ws;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      Logger.error(`[${EXCHANGE_ID}OrderBookWS] Failed to create WebSocket connection:`, errorMessage, err);
      isConnecting.current = false;
      setError("Failed to create WebSocket connection");
    }
  }, [pairSymbol, enabled, disconnectWs, handleOrderBookUpdate, subscribeToOrderBook, createSnapshotFromState]);

  useEffect(() => {
    if (!enabled || !pairSymbol) {
      disconnectWs();
      return;
    }

    connectWs();

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        disconnectWs();
      } else if (enabled && pairSymbol) {
        connectWs();
      }
    };

    window.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      window.removeEventListener("visibilitychange", onVisibilityChange);
      disconnectWs();
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current);
      }
    };
  }, [pairSymbol, enabled, connectWs, disconnectWs]);

  return {
    isConnected,
    orderBookData: orderBookData ?? undefined,
    error,
  };
};
